import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { PlusIcon, XIcon } from './icons';
import { Badge, Button, CopyButton, ErrorText, cx } from './ui';
import { track } from '../lib/amplitude';
import { ApiError, errorMessage } from '../lib/api';
import { dollars, shortDate } from '../lib/format';
import type { OrgDetail, OrgInvite, OrgMember, Role, SeatLevel } from '../lib/types';
import { qk } from '../queries/keys';
import { ROLES, inviteMember, previewSeats, resendOrgInvite, revokeInvite, roleLabel, setMemberRole, setMemberSeat, setSeatCount, type SeatQuote } from '../queries/orgs';

/**
 * The people on the team, one list: members and the invitations still out,
 * each row carrying its own state and controls. The seat count sits in the
 * header with the one way to grow it. Shared by the members page and the
 * subscription tab, since seats are people.
 *
 * The API is the authority on who may change whom: an admin trying to demote
 * an owner gets a 403 with a sentence in it, and that sentence is what we show.
 * The only rule duplicated here is hiding the controls on your own row, because
 * offering someone a button that removes themselves is just unkind.
 */

function Avatar({ name, email, avatarUrl }: { name: string | null; email: string; avatarUrl?: string | null }) {
  const initial = (name?.trim() || email).charAt(0).toUpperCase();
  return avatarUrl ? (
    <img src={avatarUrl} alt="" referrerPolicy="no-referrer" className="h-8 w-8 shrink-0 rounded-full object-cover" />
  ) : (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-700 text-caption font-medium text-chalk">
      {initial}
    </span>
  );
}

function Row({ children, muted = false }: { children: ReactNode; muted?: boolean }) {
  return (
    <li className={cx('flex flex-wrap items-center gap-3 border-b border-hairline py-3 last:border-b-0', muted && 'opacity-80')}>
      {children}
    </li>
  );
}

function RoleSelect({ value, disabled, label, onChange }: { value: Role; disabled?: boolean; label: string; onChange: (role: Role) => void }) {
  return (
    <select
      value={value}
      disabled={disabled}
      aria-label={label}
      onChange={(event) => onChange(event.target.value as Role)}
      className="h-8 rounded border border-hairline bg-slate-900 px-2 text-caption text-chalk transition-colors hover:border-slate-600 focus:border-signal focus:outline-none disabled:opacity-50"
    >
      {ROLES.map((role) => (
        <option key={role} value={role}>
          {roleLabel(role)}
        </option>
      ))}
    </select>
  );
}

const SEATS: { value: SeatLevel; label: string }[] = [
  { value: 'paid', label: 'Paid seat' },
  { value: 'free', label: 'Free' },
];

function SeatSelect({ value, disabled, label, onChange }: { value: SeatLevel; disabled?: boolean; label: string; onChange: (seat: SeatLevel) => void }) {
  return (
    <select
      value={value}
      disabled={disabled}
      aria-label={label}
      onChange={(event) => onChange(event.target.value as SeatLevel)}
      className="h-8 rounded border border-hairline bg-slate-900 px-2 text-caption text-chalk transition-colors hover:border-slate-600 focus:border-signal focus:outline-none disabled:opacity-50"
    >
      {SEATS.map((seat) => (
        <option key={seat.value} value={seat.value}>
          {seat.label}
        </option>
      ))}
    </select>
  );
}

function MemberRow({ orgId, member, isSelf }: { orgId: string; member: OrgMember; isSelf: boolean }) {
  const client = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const invalidate = () => {
    void client.invalidateQueries({ queryKey: qk.org(orgId) });
    void client.invalidateQueries({ queryKey: qk.orgs() });
  };
  const changeRole = useMutation({
    mutationFn: (role: Role) => setMemberRole(orgId, member.user.id, role),
    onSuccess: (_result, role) => {
      track('Member Role Changed', { to: role });
      invalidate();
    },
  });
  const changeSeat = useMutation({
    mutationFn: (seat: SeatLevel) => setMemberSeat(orgId, member.user.id, seat),
    onSuccess: (_result, seat) => {
      track('Member Seat Changed', { to: seat });
      invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: () => setMemberRole(orgId, member.user.id, null),
    onSuccess: () => {
      track('Member Removed');
      setConfirming(false);
      invalidate();
    },
  });
  const busy = changeRole.isPending || changeSeat.isPending || remove.isPending;
  const failure = changeRole.error ?? changeSeat.error ?? remove.error;

  return (
    <Row>
      <Avatar name={member.user.name} email={member.user.email} avatarUrl={member.user.avatarUrl} />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 truncate text-body text-chalk">
          <span className="truncate">{member.user.name ?? member.user.email}</span>
          {isSelf ? <Badge tone="neutral">You</Badge> : null}
        </p>
        <p className="truncate text-caption text-chalk-faint">
          {member.user.email} · joined {shortDate(member.createdAt)}
        </p>
      </div>
      {isSelf ? (
        <span className="text-caption text-chalk-dim">
          {roleLabel(member.role)} · {member.seat === 'paid' ? 'Paid seat' : 'Free'}
        </span>
      ) : (
        <>
          <RoleSelect value={member.role} disabled={busy} label={`Role for ${member.user.email}`} onChange={(role) => changeRole.mutate(role)} />
          {/* Owners and admins run the team, so they are always on a paid seat. */}
          {member.role === 'user' ? (
            <SeatSelect value={member.seat} disabled={busy} label={`Seat for ${member.user.email}`} onChange={(seat) => changeSeat.mutate(seat)} />
          ) : (
            <span className="text-caption text-chalk-dim">Paid seat</span>
          )}
          {confirming ? (
            <span className="flex items-center gap-1.5">
              <Button variant="danger" size="sm" busy={remove.isPending} onClick={() => remove.mutate()}>
                Remove
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                Keep
              </Button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              disabled={busy}
              aria-label={`Remove ${member.user.email}`}
              title="Remove from the team"
              className="flex h-8 w-8 items-center justify-center rounded text-chalk-faint hover:bg-slate-800 hover:text-chalk"
            >
              <XIcon size={14} />
            </button>
          )}
        </>
      )}
      {failure ? (
        <div className="w-full">
          <ErrorText>{errorMessage(failure)}</ErrorText>
        </div>
      ) : null}
    </Row>
  );
}

function InviteRow({ orgId, invite }: { orgId: string; invite: OrgInvite }) {
  const client = useQueryClient();
  const revoke = useMutation({
    mutationFn: () => revokeInvite(orgId, invite.id),
    onSuccess: () => {
      track('Invite Revoked');
      void client.invalidateQueries({ queryKey: qk.org(orgId) });
    },
  });
  // The button says "Sent" for a moment after, so the press visibly did
  // something; the row's "sent" date updates underneath it as well.
  const [justSent, setJustSent] = useState(false);
  const resend = useMutation({
    mutationFn: () => resendOrgInvite(orgId, invite.id),
    onSuccess: () => {
      track('Invite Resent');
      setJustSent(true);
      void client.invalidateQueries({ queryKey: qk.org(orgId) });
    },
  });
  useEffect(() => {
    if (!justSent) return;
    const timer = window.setTimeout(() => setJustSent(false), 2500);
    return () => window.clearTimeout(timer);
  }, [justSent]);
  // Only the people who can send one can send it again: the link is what
  // tells them apart.
  const mayManage = invite.acceptUrl !== null;
  const failure = resend.error ?? revoke.error;

  return (
    <Row muted>
      <Avatar name={invite.name ?? null} email={invite.email} />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 truncate text-body text-chalk">
          <span className="truncate">{invite.name?.trim() || invite.email}</span>
          <Badge tone="caution">Invited</Badge>
        </p>
        <p className="truncate text-caption text-chalk-faint">
          {invite.name?.trim() ? `${invite.email} · ` : ''}sent {shortDate(invite.lastSentAt ?? invite.createdAt)} · expires {shortDate(invite.expiresAt)}
          {invite.seat === 'paid' ? ' · holds a paid seat' : ''}
        </p>
      </div>
      <span className="text-caption text-chalk-dim">
        {roleLabel(invite.role)} · {invite.seat === 'paid' ? 'Paid seat' : 'Free'}
      </span>
      {mayManage ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          busy={resend.isPending}
          disabled={justSent}
          onClick={() => resend.mutate()}
          aria-label={`Send the invitation to ${invite.email} again`}
        >
          {justSent ? 'Sent' : 'Resend'}
        </Button>
      ) : null}
      {invite.acceptUrl ? (
        <CopyButton
          value={invite.acceptUrl}
          label="Copy link"
          copiedLabel="Copied"
          variant="ghost"
          size="sm"
          onCopied={() => track('Invite Link Copied')}
        />
      ) : null}
      <button
        type="button"
        onClick={() => revoke.mutate()}
        disabled={revoke.isPending}
        aria-label={`Revoke the invitation for ${invite.email}`}
        title="Revoke"
        className="flex h-8 w-8 items-center justify-center rounded text-chalk-faint hover:bg-slate-800 hover:text-chalk"
      >
        <XIcon size={14} />
      </button>
      {failure ? (
        <div className="w-full">
          <ErrorText>{errorMessage(failure)}</ErrorText>
        </div>
      ) : null}
    </Row>
  );
}

/**
 * The moment of purchase. Buying or releasing seats shows the exact
 * number from Stripe before anything changes: what is due today, what
 * the plan costs from here, and when. No separate checkout; the card on
 * file is charged the way every seat-based plan does it, but never
 * without this screen.
 */
export function SeatChangeDialog({
  orgId,
  seats,
  onDone,
  onClose,
}: {
  orgId: string;
  /** The count to change to. */
  seats: number;
  onDone: () => void;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const quote = useQuery({ queryKey: ['seat-quote', orgId, seats], queryFn: () => previewSeats(orgId, seats) });
  const change = useMutation({
    mutationFn: () => setSeatCount(orgId, seats),
    onSuccess: (result) => {
      track('Seats Changed', { from: quote.data?.current ?? result.seats, to: seats });
      void client.invalidateQueries({ queryKey: qk.org(orgId) });
      void client.invalidateQueries({ queryKey: qk.orgs() });
      onDone();
    },
  });

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const q: SeatQuote | undefined = quote.data;
  const adding = q ? q.seats > q.current : true;
  const delta = q ? Math.abs(q.seats - q.current) : 1;
  const title = adding
    ? `Add ${delta === 1 ? 'a paid seat' : `${delta} paid seats`}`
    : `Remove ${delta === 1 ? 'a paid seat' : `${delta} paid seats`}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="seat-change-title"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/70 px-4 backdrop-blur-[2px]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-sm rounded-md border border-hairline bg-slate-900 p-6 shadow-raised">
        <button type="button" onClick={onClose} aria-label="Close" className="absolute right-3 top-3 rounded p-1 text-chalk-faint hover:text-chalk">
          <XIcon size={16} />
        </button>
        <h2 id="seat-change-title" className="text-h4 text-chalk">
          {title}
        </h2>

        {quote.isPending ? <p className="mt-3 text-body text-chalk-dim">Getting the number from Stripe.</p> : null}
        {quote.isError ? (
          <div className="mt-3">
            <ErrorText>{errorMessage(quote.error)}</ErrorText>
          </div>
        ) : null}

        {q ? (
          <dl className="mt-4 divide-y divide-hairline rounded border border-hairline">
            <div className="flex items-baseline justify-between gap-3 px-3 py-2.5">
              <dt className="text-caption text-chalk-dim">Paid seats</dt>
              <dd className="text-body text-chalk tnum">
                {q.current} <span className="text-chalk-faint">to</span> {q.seats}
                {q.comped ? (
                  <span className="block text-caption text-chalk-faint">
                    plus {q.comped} on us, which {q.comped === 1 ? 'stays' : 'stay'} either way
                  </span>
                ) : null}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-3 px-3 py-2.5">
              <dt className="text-caption text-chalk-dim">Due today</dt>
              <dd className="text-body text-chalk tnum">
                {q.billed ? dollars(q.dueNowCents) : '$0'}
                {q.billed && adding && q.dueNowCents > 0 ? (
                  <span className="block text-caption text-chalk-faint">prorated to {q.periodEnd ? shortDate(q.periodEnd) : 'the period end'}</span>
                ) : null}
                {q.billed && !adding ? <span className="block text-caption text-chalk-faint">credit applied to the next invoice</span> : null}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-3 px-3 py-2.5">
              <dt className="text-caption text-chalk-dim">{q.billed ? `From ${q.periodEnd ? shortDate(q.periodEnd) : 'next period'}` : 'After the trial'}</dt>
              <dd className="text-body text-chalk tnum">{dollars(q.monthlyCents)} a month</dd>
            </div>
          </dl>
        ) : null}

        {q && !q.billed ? (
          <p className="mt-3 text-caption text-chalk-faint">
            Nothing is charged during the trial{q.periodEnd ? `, which ends ${shortDate(q.periodEnd)}` : ''}. Set up billing before then to keep the seats.
          </p>
        ) : null}
        {change.isError ? (
          <div className="mt-3">
            <ErrorText>{errorMessage(change.error)}</ErrorText>
          </div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <Button onClick={onClose} disabled={change.isPending}>
            Cancel
          </Button>
          <Button variant="primary" busy={change.isPending} disabled={!q} onClick={() => change.mutate()}>
            {q?.billed && adding && q.dueNowCents > 0 ? `Pay ${dollars(q.dueNowCents)} and add` : adding ? 'Add seat' : 'Remove seat'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Adding people: paste a list of addresses, however they were copied, and
 * each one becomes a row you can name and put on a seat.
 *
 * The paste is the input everyone actually has. Addresses arrive from a
 * spreadsheet column, an email client ("Sam Rivera <sam@co.com>"), a Slack
 * message, separated by commas or spaces or nothing but newlines, so the box
 * takes all of it and keeps only what is left over as you type. One at a time
 * still works: type an address, press Enter.
 */

const EMAIL_RE = /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/;

export interface InviteDraft {
  /** Stable across re-parses, so editing a name does not jump rows. */
  key: string;
  email: string;
  first: string;
  last: string;
  role: Role;
  seat: SeatLevel;
  status: 'ready' | 'sending' | 'sent' | 'failed';
  acceptUrl?: string;
  error?: string;
}

/** Title Case for a name pulled out of an address, left alone otherwise. */
function titleCase(word: string): string {
  if (!word) return '';
  return word[0].toUpperCase() + word.slice(1);
}

/**
 * A name from the address itself, for the common shapes: `sam.rivera@`,
 * `sam_rivera@`, `sam-rivera@`. Anything else (`srivera`, `info`) is left for
 * the person to fill in rather than guessed at badly.
 */
function nameFromEmail(email: string): { first: string; last: string } {
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[._-]+/).filter((part) => /^[a-z]+$/i.test(part));
  if (parts.length < 2) return { first: '', last: '' };
  return { first: titleCase(parts[0]), last: titleCase(parts[parts.length - 1]) };
}

/**
 * Every address in a blob of pasted text, with the name that came with it
 * where one did. Anything that is not an address is dropped: pasted text is
 * full of "To:", tabs and stray punctuation, and none of it is a person.
 */
export function parseInvites(text: string): Array<{ email: string; first: string; last: string }> {
  const out: Array<{ email: string; first: string; last: string }> = [];
  // "Sam Rivera <sam@co.com>" first, so the name is not lost to the split.
  const angled = /([^<>,;\n]*?)<\s*([^<>\s,;]+)\s*>/g;
  let rest = text;
  for (const match of text.matchAll(angled)) {
    const email = match[2].trim().toLowerCase();
    if (!EMAIL_RE.test(email)) continue;
    const words = match[1].trim().replace(/^["']|["']$/g, '').split(/\s+/).filter(Boolean);
    out.push({
      email,
      first: words[0] ?? nameFromEmail(email).first,
      last: words.length > 1 ? words.slice(1).join(' ') : nameFromEmail(email).last,
    });
    rest = rest.replace(match[0], ' ');
  }
  for (const token of rest.split(/[\s,;]+/)) {
    const email = token.trim().replace(/^[<"']+|[>"',.]+$/g, '').toLowerCase();
    if (!email || !EMAIL_RE.test(email)) continue;
    out.push({ email, ...nameFromEmail(email) });
  }
  return out;
}

function AddUserDialog({
  orgId,
  seatPrice,
  seats,
  seatsUsed,
  isOwner,
  existing,
  onClose,
}: {
  orgId: string;
  seatPrice: string | null;
  seats: number;
  seatsUsed: number;
  isOwner: boolean;
  /** Addresses already on the team or already invited, lowercased. */
  existing: Set<string>;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const spare = Math.max(0, seats - seatsUsed);
  const refresh = () => {
    void client.invalidateQueries({ queryKey: qk.org(orgId) });
    void client.invalidateQueries({ queryKey: qk.orgs() });
  };

  const [text, setText] = useState('');
  const [drafts, setDrafts] = useState<InviteDraft[]>([]);
  const [buying, setBuying] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /**
   * Take the finished addresses out of the box and leave what is still being
   * typed. `all` is for the moments the person has plainly finished with it:
   * a paste, Enter, leaving the field. Without it the last token is held
   * back, because half a typed address is not an address yet and yanking it
   * into a row mid-keystroke is maddening.
   */
  const absorb = (value: string, all: boolean) => {
    const finished = all || /[\s,;>]$/.test(value);
    const source = finished ? value : value.slice(0, Math.max(0, value.lastIndexOf(' ') + 1));
    const leftover = finished ? '' : value.slice(source.length);
    const found = parseInvites(source);
    if (found.length === 0) {
      setText(value);
      return;
    }
    setDrafts((current) => {
      const seen = new Set(current.map((draft) => draft.email));
      const added: InviteDraft[] = [];
      for (const [index, one] of found.entries()) {
        if (seen.has(one.email)) continue;
        seen.add(one.email);
        // A paid seat, always: people are added to a team to use it. Where
        // that needs seats the team has not bought, the line under the list
        // says how many and the button buys them before anything goes out,
        // so the cost is never a surprise but is never in the way either.
        added.push({ key: `${one.email}-${Date.now()}-${index}`, ...one, role: 'user', seat: 'paid', status: 'ready' });
      }
      return [...current, ...added];
    });
    setText(leftover);
    setNotice(null);
  };

  const update = (key: string, patch: Partial<InviteDraft>) =>
    setDrafts((current) => current.map((draft) => (draft.key === key ? { ...draft, ...patch } : draft)));

  const pending = drafts.filter((draft) => draft.status !== 'sent');
  /** A row's seat, with the one rule the API also enforces: running the team is a paid job. */
  const seatOf = (draft: InviteDraft): SeatLevel => (draft.role === 'user' ? draft.seat : 'paid');
  const paidWanted = pending.filter((draft) => seatOf(draft) === 'paid').length;
  const shortBy = Math.max(0, paidWanted - spare);

  /**
   * One at a time, in order. Sequential because each paid invitation takes a
   * seat as it goes out: fired together, the last few would race the seat
   * check and come back refused for no reason the person could see.
   */
  const send = useMutation({
    mutationFn: async () => {
      for (const draft of drafts) {
        if (draft.status === 'sent') continue;
        update(draft.key, { status: 'sending', error: undefined });
        const name = [draft.first.trim(), draft.last.trim()].filter(Boolean).join(' ');
        try {
          const response = await inviteMember(orgId, {
            email: draft.email,
            name: name || undefined,
            role: draft.role,
            seat: seatOf(draft),
          });
          update(draft.key, { status: 'sent', acceptUrl: response.invite.acceptUrl });
          track('Member Invited', { role: draft.role, seat: seatOf(draft) });
        } catch (error) {
          const code = error instanceof ApiError ? (error.details as { code?: string } | undefined)?.code : undefined;
          update(draft.key, {
            status: 'failed',
            error: code === 'no_spare_seat' ? 'No seat spare' : errorMessage(error),
          });
        }
      }
      refresh();
    },
  });

  const sent = drafts.filter((draft) => draft.status === 'sent');
  const failed = drafts.filter((draft) => draft.status === 'failed');
  const busy = send.isPending;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-user-title"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/70 px-4 py-10 backdrop-blur-[2px]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-3xl rounded-md border border-hairline bg-slate-900 p-6 shadow-raised">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 rounded p-1 text-chalk-faint hover:text-chalk"
        >
          <XIcon size={16} />
        </button>
        <h2 id="add-user-title" className="text-h4 text-chalk">
          Add users
        </h2>
        <p className="mt-1 text-caption text-chalk-dim">
          Paste addresses separated by commas, spaces or new lines. Each one becomes a row you can name and put on a
          seat. Everyone gets a link to join.
        </p>

        <form
          className="mt-5 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            absorb(text, true);
            if (drafts.length === 0) {
              setNotice('Paste at least one email address.');
              return;
            }
            if (shortBy > 0) {
              if (isOwner) setBuying(seats + shortBy);
              else setNotice('Every paid seat is taken. Ask an owner to add one, or move these to free seats.');
              return;
            }
            send.mutate();
          }}
        >
          <div className="space-y-1.5">
            <label htmlFor="invite-emails" className="block text-caption font-medium text-chalk-dim">
              Emails
            </label>
            <textarea
              id="invite-emails"
              autoFocus
              rows={2}
              value={text}
              disabled={busy}
              placeholder="sam@company.com, dana@company.com&#10;Ali Reyes <ali@company.com>"
              onChange={(event) => absorb(event.target.value, false)}
              onBlur={(event) => absorb(event.target.value, true)}
              onPaste={(event) => {
                // A paste is a finished list, whatever it ends with. Without
                // this the last address, having no separator after it, would
                // be left behind in the box looking like a typo.
                event.preventDefault();
                const pasted = event.clipboardData.getData('text');
                const joined = text && !/[\s,;]$/.test(text) ? `${text} ${pasted}` : `${text}${pasted}`;
                absorb(joined, true);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  absorb(text, true);
                }
              }}
              className="w-full resize-y rounded border border-hairline bg-slate-900 px-3 py-2 text-body text-chalk placeholder:text-chalk-faint transition-colors hover:border-slate-600 focus:border-signal focus:outline-none disabled:opacity-50"
            />
          </div>

          {drafts.length > 0 ? (
            <div className="overflow-hidden rounded border border-hairline">
              <div className="grid grid-cols-[1fr_6.5rem_6.5rem_7rem_6.5rem_2rem] items-center gap-2 border-b border-hairline bg-slate-950/40 px-3 py-2 text-micro uppercase tracking-[0.08em] text-chalk-faint">
                <span>Email</span>
                <span>First name</span>
                <span>Last name</span>
                <span>Role</span>
                <span>Seat</span>
                <span className="sr-only">Remove</span>
              </div>
              <ul>
                {drafts.map((draft) => {
                  const already = existing.has(draft.email);
                  return (
                    <li
                      key={draft.key}
                      className="grid grid-cols-[1fr_6.5rem_6.5rem_7rem_6.5rem_2rem] items-center gap-2 border-b border-hairline px-3 py-2 last:border-b-0"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-body text-chalk" title={draft.email}>
                          {draft.email}
                        </span>
                        {draft.status === 'sent' ? (
                          <span className="text-caption text-go">Invited</span>
                        ) : draft.status === 'failed' ? (
                          <span className="text-caption text-danger">{draft.error}</span>
                        ) : already ? (
                          <span className="text-caption text-caution">Already on this team</span>
                        ) : null}
                      </span>
                      <input
                        aria-label={`First name for ${draft.email}`}
                        value={draft.first}
                        maxLength={40}
                        disabled={busy || draft.status === 'sent'}
                        onChange={(event) => update(draft.key, { first: event.target.value })}
                        className="h-8 w-full rounded border border-hairline bg-slate-950 px-2 text-caption text-chalk focus:border-signal focus:outline-none disabled:opacity-50"
                      />
                      <input
                        aria-label={`Last name for ${draft.email}`}
                        value={draft.last}
                        maxLength={40}
                        disabled={busy || draft.status === 'sent'}
                        onChange={(event) => update(draft.key, { last: event.target.value })}
                        className="h-8 w-full rounded border border-hairline bg-slate-950 px-2 text-caption text-chalk focus:border-signal focus:outline-none disabled:opacity-50"
                      />
                      <RoleSelect
                        value={draft.role}
                        disabled={busy || draft.status === 'sent'}
                        label={`Role for ${draft.email}`}
                        onChange={(role) => update(draft.key, { role })}
                      />
                      <SeatSelect
                        value={seatOf(draft)}
                        // Running the team is a paid job, so an admin or an
                        // owner has no seat to choose. The API says the same.
                        disabled={busy || draft.status === 'sent' || draft.role !== 'user'}
                        label={`Seat for ${draft.email}`}
                        onChange={(seat) => update(draft.key, { seat })}
                      />
                      <button
                        type="button"
                        aria-label={`Remove ${draft.email}`}
                        disabled={busy}
                        onClick={() => setDrafts((current) => current.filter((one) => one.key !== draft.key))}
                        className="rounded p-1 text-chalk-faint hover:text-chalk disabled:opacity-40"
                      >
                        <XIcon size={14} />
                      </button>
                    </li>
                  );
                })}
              </ul>
              {sent.length > 0 ? (
                <div className="space-y-2 border-t border-hairline bg-slate-950/40 px-3 py-2.5">
                  <p className="text-caption text-chalk-dim">
                    {sent.length} {sent.length === 1 ? 'invitation is' : 'invitations are'} ready. Only the person
                    addressed can accept a link.
                  </p>
                  {sent.map((draft) => (
                    <div key={draft.key} className="flex items-center gap-2">
                      <span className="min-w-0 flex-1 truncate font-mono text-caption text-chalk-faint">{draft.acceptUrl}</span>
                      <CopyButton
                        value={draft.acceptUrl ?? ''}
                        label="Copy"
                        copiedLabel="Copied"
                        variant="ghost"
                        size="sm"
                        onCopied={() => track('Invite Link Copied')}
                      />
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          <p className="text-caption text-chalk-faint">
            {pending.length > 0 ? (
              <>
                {paidWanted} paid {paidWanted === 1 ? 'seat' : 'seats'} needed, {spare} spare
                {seatPrice && shortBy > 0 ? ` · ${shortBy} more at ${seatPrice}/mo each` : ''}.{' '}
              </>
            ) : null}
            A free seat costs nothing: they can watch and comment, and their own recordings stay on the free plan.
            Admins and owners run the team, so they are always on a paid one.
          </p>

          {notice ? <ErrorText>{notice}</ErrorText> : null}
          {failed.length > 0 && !busy ? (
            <ErrorText>
              {failed.length} {failed.length === 1 ? 'invitation' : 'invitations'} did not go out. Fix what the row says
              and send again; the ones that went are not sent twice.
            </ErrorText>
          ) : null}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" onClick={onClose} disabled={busy}>
              {sent.length > 0 ? 'Done' : 'Cancel'}
            </Button>
            <Button type="submit" variant="primary" busy={busy} disabled={pending.length === 0}>
              {shortBy > 0 && isOwner
                ? `Add ${shortBy} ${shortBy === 1 ? 'seat' : 'seats'} and invite`
                : pending.length > 1
                  ? `Send ${pending.length} invites`
                  : 'Send invite'}
            </Button>
          </div>

          {buying !== null ? (
            <SeatChangeDialog
              orgId={orgId}
              seats={buying}
              onDone={() => {
                setBuying(null);
                send.mutate();
              }}
              onClose={() => setBuying(null)}
            />
          ) : null}
        </form>
      </div>
    </div>
  );
}

/** Members and invitations in one list, seats bought and used in the header. */
export function TeamRoster({
  detail,
  viewerEmail,
  canManage = true,
  isOwner = false,
  seatPrice = null,
  lapsed = false,
  canBuySeats = true,
}: {
  detail: OrgDetail;
  viewerEmail: string | null;
  /** Admins and owners; a plain member sees the list without the controls. */
  canManage?: boolean;
  /** Owners buy and release seats. */
  isOwner?: boolean;
  /**
   * False for a team that exists through a grant alone: with no subscription
   * a seat count has nothing to bill against, so the plan row's checkout is
   * the way to more seats and the inline controls would only mislead.
   */
  canBuySeats?: boolean;
  /** "$7", for the dialog and the seat line. */
  seatPrice?: string | null;
  /**
   * The subscription ended. The people who were on the team are still worth
   * showing, so you can see who you would be bringing back, but nothing here
   * can be changed until there is a plan again.
   */
  lapsed?: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const manageable = canManage && !lapsed;
  // Seats we have given this team sit on top of the ones it buys, so the
  // allowance is the two added up while the bought count stays what Stripe
  // bills for. `comped` is 0 for almost every team, and everything about it
  // below is hidden in that case.
  const paidSeats = detail.seats?.paid ?? detail.seatCount;
  const comped = detail.seats?.comped ?? 0;
  const seats = detail.seats?.total ?? detail.seatCount;
  const used = detail.seatsUsed ?? detail.memberships.filter((m) => m.seat === 'paid').length;
  const free = detail.memberships.filter((m) => m.seat === 'free').length;
  const pending = detail.invites.length;

  const [changingTo, setChangingTo] = useState<number | null>(null);

  return (
    <section className="card p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-h4 text-chalk">Users</h2>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-caption text-chalk-faint tnum">
            <span>
              {used} of {seats} paid {seats === 1 ? 'seat' : 'seats'} used
              {comped > 0 ? ` · ${comped} on us` : ''}
              {free > 0 ? ` · ${free} free` : ''}
              {pending > 0 ? ` · ${pending} invited` : ''}
            </span>
            {isOwner && !lapsed && canBuySeats ? (
              <span className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setChangingTo(paidSeats + 1)}
                  title={seatPrice ? `Add a paid seat, ${seatPrice} a month` : 'Add a paid seat'}
                  className="rounded px-1.5 py-0.5 text-signal hover:bg-slate-800 disabled:opacity-50"
                >
                  + Add seat
                </button>
                {/* A bought seat can go only while the team would still fit:
                    the ones on us hold people too. */}
                {paidSeats > 1 && seats > used ? (
                  <button
                    type="button"
                    onClick={() => setChangingTo(paidSeats - 1)}
                    title="Stop paying for an unused seat"
                    className="rounded px-1.5 py-0.5 text-chalk-dim hover:bg-slate-800 hover:text-chalk disabled:opacity-50"
                  >
                    Remove seat
                  </button>
                ) : null}
              </span>
            ) : null}
          </p>
        </div>
        {manageable ? (
          <Button size="sm" onClick={() => setAdding(true)}>
            <PlusIcon size={13} />
            Add user
          </Button>
        ) : null}
      </div>

      {lapsed ? (
        <p className="mt-3 rounded border border-caution/35 bg-caution/[0.07] px-3 py-2 text-caption text-chalk-dim">
          Subscribe to manage your team. These are the people who were on it; nobody can be added,
          removed or changed until the plan is running again.
        </p>
      ) : null}

      <ul className={cx('mt-3', lapsed && 'pointer-events-none opacity-50')} aria-disabled={lapsed || undefined}>
        {detail.memberships.map((member) => (
          <MemberRow
            key={member.user.id}
            orgId={detail.id}
            member={member}
            isSelf={!manageable || (viewerEmail !== null && member.user.email.toLowerCase() === viewerEmail)}
          />
        ))}
        {detail.invites.map((invite) => (
          <InviteRow key={invite.id} orgId={detail.id} invite={invite} />
        ))}
      </ul>
      {changingTo !== null ? (
        <SeatChangeDialog orgId={detail.id} seats={changingTo} onDone={() => setChangingTo(null)} onClose={() => setChangingTo(null)} />
      ) : null}
      {adding ? (
        <AddUserDialog
          orgId={detail.id}
          seatPrice={seatPrice}
          seats={seats}
          seatsUsed={used}
          isOwner={isOwner}
          existing={
            new Set([
              ...detail.memberships.map((m) => m.user.email.toLowerCase()),
              ...detail.invites.map((i) => i.email.toLowerCase()),
            ])
          }
          onClose={() => setAdding(false)}
        />
      ) : null}
    </section>
  );
}
